import AsyncStorage from '@react-native-async-storage/async-storage';
import { Picker } from '@react-native-picker/picker';
import axios from 'axios';
import { Formik } from 'formik';
import { confirmAlert } from 'react-confirm-alert';
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Image,
  LogBox,
  Pressable,
  Alert,
  Modal,
  TouchableWithoutFeedback,
} from 'react-native';
if (Platform.OS === 'web') {
  require('react-confirm-alert/src/react-confirm-alert.css');
}
import { responsiveWidth } from 'react-native-responsive-dimensions';
import { Table, TableWrapper, Row, Cell } from 'react-native-table-component';
import { globalStyles } from '../../../styles/globalStyles';
import { BASE_URL } from '../../../components/BaseUrl';
import AppBtn from '../../../shared/appBtn';
import AnimatedArrow from '../../../shared/AnimatedArrow';
import WavyTopBar from '../../../shared/WavyTopBar';
import ImageGallery from '../../../shared/ImageGallery';
import LeftBar from '../../../shared/LeftBar';
import LeftBarItem from '../../../shared/LeftBarItem';
import LeftBarItemAdmin from '../../../shared/LeftBarItemAdmin';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import moment from 'moment';
import ErrorText from '../../../shared/errorText';
import CustomDropdown from '../../../shared/CustomDropdown';
import { DatePickerModal } from 'react-native-paper-dates';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import * as yup from 'yup';
import {
  toBanglaNumerals,
  toBanglaDateTimeString,
  toBanglaDayName,
} from '../../../shared/banglaLocalization';

// ---------------------------------------------------------------------------
// Module-level constants
// These used to be re-created inside the component body on every render.
// Hoisting them out avoids the pointless re-allocation and makes the static
// nature of the data explicit.
// ---------------------------------------------------------------------------

const windowHeight = Dimensions.get('window').height;
const windowWidth = Dimensions.get('window').width;
const IS_DESKTOP_FORM = Platform.OS === 'web' && windowWidth >= 1024;
const IS_TABLET_FORM = Platform.OS === 'web' && windowWidth >= 600 && windowWidth < 1024;
const TABLE_WIDTH = windowWidth - 220;
const LOCALE = 'en-GB';

// District ids that use the metropolitan court hierarchy instead of the
// standard district court hierarchy.
const METRO_DISTRICT_IDS = [65, 66, 67, 68, 69, 70];

// Court "category" options, scoped by whether the district is metropolitan.
const DISTRICT_COURT_CATEGORIES = [
  {
    id: '2',
    name: 'জেলা জজ আদালত',
    office_origin_ids: [4, 5, 6, 7, 8, 18, 19, 20, 21, 74, 75, 76, 83, 94, 103, 105],
    type: '1',
  },
  {
    id: '3',
    name: 'চীফ জুডিসিয়াল ম্যাজিস্ট্রেট আদালত',
    office_origin_ids: [14, 15, 16, 17, 84, 85, 91, 95],
    type: '1',
  },
  {
    id: '4',
    name: 'ট্রাইবুনালসমূহ',
    office_origin_ids: [89, 9, 72, 73, 77, 79, 80, 81, 82, 86, 87, 96, 102, 104, 110, 111, 112],
    type: '1',
  },
];

const METRO_COURT_CATEGORIES = [
  {
    id: '5',
    name: 'মহানগর দায়রা জজ আদালত',
    office_origin_ids: [10, 11, 12, 88],
    type: '2',
  },
  {
    id: '6',
    name: 'চীফ মেট্রোপলিটন ম্যাজিস্ট্রেট আদালত',
    office_origin_ids: [13, 22, 23, 24, 78, 92, 100],
    type: '2',
  },
  {
    id: '7',
    name: 'বিশেষ ট্রাইব্যুনাল',
    office_origin_ids: [109],
    type: '2',
  },
];

const searchFormValidationSchema = yup.object({
  division_name: yup.string().required('Division is required'),
  district_name: yup.string().required('District is required'),
  lower_court_name: yup.string().required('Lower Court is required'),
});

const PAST_DATE_LIMIT = new Date(new Date().setDate(new Date().getDate() - 500000));
const FUTURE_DATE_LIMIT = new Date(new Date().setDate(new Date().getDate() + 500000));

const TABLE_COLUMN_TITLES = [
  'ক্রমিক নং',
  'মামলার নম্বর',
  'কার্যক্রম',
  'পরবর্তী তারিখ',
  'সংক্ষিপ্ত আদেশ',
];

function computeTableColumnWidths() {
  const totalWidth = windowWidth - 40;
  return [
    totalWidth * 0.05, // serial
    totalWidth * 0.25, // case number
    totalWidth * 0.1, // activity
    totalWidth * 0.08, // next date
    totalWidth * 0.3, // order
  ];
}

const TABLE_COLUMN_WIDTHS = computeTableColumnWidths();

function isPlaceholderValue(value) {
  return value == null;
}

export default function OnlineCaseListScreen() {
  const navigation = useNavigation();
  const route = useRoute();

  // ---------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------
  const [caseInfoList, setCaseInfoList] = useState([]);
  const [isFutureDateSearch, setIsFutureDateSearch] = useState(0);
  const [districtName, setDistrictName] = useState('');
  const [isMobileDatePickerVisible, setIsMobileDatePickerVisible] = useState(false);
  const [selectedMobileDate, setSelectedMobileDate] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchExecuted, setIsSearchExecuted] = useState(false);
  const [lowerCourtOptions, setLowerCourtOptions] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [divisionId, setDivisionId] = useState(null);
  const [districtId, setDistrictId] = useState(null);
  const [courtCategoryId, setCourtCategoryId] = useState(null);
  const [policeStationId, setPoliceStationId] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [policeStationOptions, setPoliceStationOptions] = useState([]);
  const [courtCategoryOptions, setCourtCategoryOptions] = useState([]);
  const [allCourtsInDistrict, setAllCourtsInDistrict] = useState([]);
  const [activeTab, setActiveTab] = useState('files');
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [causeListImages, setCauseListImages] = useState([]);
  const [selectedCourtName, setSelectedCourtName] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLowerCourtDropdownOpen, setIsLowerCourtDropdownOpen] = useState(false);
  const [selectedCourtId, setSelectedCourtId] = useState(null);

  // Web-only date picker (react-native-paper-dates) state.
  const [isWebDatePickerVisible, setIsWebDatePickerVisible] = useState(false);
  const [selectedWebDate, setSelectedWebDate] = useState(new Date());

  const responsiveFieldWidth = IS_DESKTOP_FORM ? 250 : '100%';

  const lowerCourtOptionsWithPlaceholder = useMemo(
    () => [
      { label: 'আদালত নির্বাচন করুন', value: '', labelStyle: { color: 'red' } },
      ...(lowerCourtOptions || []).map(court => ({
        ...court,
        value: court.value?.toString() || court.id?.toString(),
        label: court.label || court.office_name_bng,
      })),
    ],
    [lowerCourtOptions],
  );

  // ---------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------

  const fetchDivisions = async () => {
    try {
      const url = `${BASE_URL}/GetDivission`;
      return await axios.get(url).then(res => res.data);
    } catch (err) {
      console.log('fetchDivisions Error:', err);
    }
  };

  const fetchDistrictsByDivision = async division => {
    try {
      const formBody = new URLSearchParams({ division_id: division }).toString();
      const url = `${BASE_URL}/DivisionToDistrict`;
      return await axios.post(url, formBody).then(res => res.data);
    } catch (err) {
      console.log('fetchDistrictsByDivision Error:', err);
    }
  };

  const loadDistrictsForDivision = selectedDivisionId => {
    fetchDistrictsByDivision(selectedDivisionId)
      .then(fetchedDistricts => setDistricts(fetchedDistricts))
      .catch(err => console.log('loadDistrictsForDivision Error:', err));
  };

  const fetchCourtsByDistrict = async selectedDistrictId => {
    try {
      const formBody = new URLSearchParams({
        geo_district_id: selectedDistrictId,
        geo_division_id: divisionId,
      }).toString();
      const url = `${BASE_URL}/DivisionAndDistrictToCourtName`;
      const response = await axios.post(url, formBody).then(res => res.data);
      setAllCourtsInDistrict(response);
    } catch (err) {
      console.log('fetchCourtsByDistrict Error:', err);
    }
  };

  /** Filters the district's full court list down to the selected court category. */
  const filterLowerCourtsByOriginIds = officeOriginIds => {
    try {
      const officeOriginSet = new Set(officeOriginIds);
      const matchingCourts = allCourtsInDistrict.filter(court =>
        officeOriginSet.has(court.office_origin_id),
      );
      setLowerCourtOptions(
        matchingCourts.map(court => ({
          ...court,
          value: court.id,
          label: court.office_name_bng,
        })),
      );
    } catch (err) {
      console.log('filterLowerCourtsByOriginIds Error:', err);
    }
  };

  const fetchPoliceStationsByGrBranch = async grBranchId => {
    try {
      const params = {
        geo_district_id: districtId,
        geo_division_id: divisionId,
        gr_branch_id: grBranchId,
        status: 1,
      };
      const url = `${BASE_URL}/GetAllPoliceStation`;
      const response = await axios.get(url, { params });
      return response.data;
    } catch (err) {
      console.log('fetchPoliceStationsByGrBranch Error:', err);
    }
  };

  const loadPoliceStationOptions = async grBranchId => {
    setPoliceStationOptions([]);
    try {
      const policeStations = await fetchPoliceStationsByGrBranch(grBranchId);
      setPoliceStationOptions(
        policeStations.map(station => ({
          ...station,
          value: station.id,
          label: station.name_bng,
        })),
      );
    } catch (err) {
      console.log('loadPoliceStationOptions Error:', err);
    }
  };

  const fetchCaseListData = async values => {
    setCaseInfoList([]);

    const searchDate =
      Platform.OS === 'web'
        ? selectedWebDate !== null
          ? moment(selectedWebDate).format('YYYY-MM-DD')
          : ''
        : selectedMobileDate !== null
          ? moment(selectedMobileDate).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD');

    // "1" is the police-station/diary based flow (GR diary search);
    // everything else is the standard lower-court based flow.
    if (Number(courtCategoryId) !== 1) {
      try {
        const formBody = new URLSearchParams({
          user_id: userId,
          division_id: divisionId,
          geo_district_id: districtId,
          lower_court_id: values.lower_court_name,
          search_date: searchDate,
        }).toString();

        const url = `${BASE_URL}/GetCaseInfoWithPaging`;
        const response = await axios.post(url, formBody).then(res => res.data);
        setIsLoading(false);
        setActiveTab('files');

        const imageResponse = await axios.get(`${BASE_URL}/GetCourtImages`, {
          params: {
            lower_court_id: values.lower_court_name,
            status: 1,
            cause_date: searchDate,
          },
        });

        if (imageResponse.status === 200) {
          setIsLoading(false);
          setCauseListImages(imageResponse.data);
        }

        return response;
      } catch (err) {
        console.log('fetchCaseListData Error:', err);
      }
    } else {
      if (!policeStationId) {
        alert('Please Select Dairy name');
        return;
      }
      try {
        const response = await axios.get(`${BASE_URL}/GetThanaImages`, {
          params: {
            diary_police_station_id: policeStationId,
            status: 1,
            cause_date: searchDate,
          },
        });

        if (response.status === 200) {
          setIsLoading(false);
          setCauseListImages(response.data);
        }
      } catch (err) {
        console.log('fetchCaseListData (diary) Error:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const submitCaseEntry = async caseInfoId => {
    try {
      const url = `${BASE_URL}/CaseEntryFromCaseInfo`;
      const formBody = new URLSearchParams({
        case_info_id: caseInfoId,
        user_id: userId,
      }).toString();
      return await axios.post(url, formBody).then(res => res.data);
    } catch (error) {
      console.log('submitCaseEntry Error:', error?.response?.data ?? error.message);
    }
  };

  const refreshCauseList = async data => {
    try {
      const formBody = new URLSearchParams(data).toString();
      const url = `${BASE_URL}/CauseListScrapUpdate`;
      return await axios.post(url, formBody).then(res => res.data);
    } catch (error) {
      console.log('refreshCauseList Error:', error);
    }
  };

  const getStoredUserId = async () => {
    try {
      const jsonValue = await AsyncStorage.getItem('user_id');
      return jsonValue !== null ? JSON.parse(jsonValue) : null;
    } catch (err) {
      console.error(err);
    }
  };

  // ---------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------

  useFocusEffect(
    useCallback(() => {
      setIsLoading(false);
      setIsSearchExecuted(false);
      setCaseInfoList([]);
    }, []),
  );

  useEffect(() => {
    if (Platform.OS === 'web') {
      window.history.pushState(null, null, document.URL);
    }

    getStoredUserId().then(storedUserId => {
      setUserId(storedUserId);

      fetchDivisions()
        .then(fetchedDivisions => setDivisions(fetchedDivisions))
        .catch(err => console.log('fetchDivisions Error:', err));
    });

    LogBox.ignoreLogs([
      'VirtualizedLists should never be nested inside plain ScrollViews with the same orientation because it can break windowing and other functionality - use another VirtualizedList-backed container instead.',
    ]);
  }, []);

  // ---------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------

  const showDatePicker = () => setIsMobileDatePickerVisible(true);
  const hideDatePicker = () => setIsMobileDatePickerVisible(false);

  const handleConfirmMobileDate = val => {
    setSelectedMobileDate(val);
    hideDatePicker();
  };

  const handleWebDateDismiss = useCallback(() => {
    setIsWebDatePickerVisible(false);
  }, []);

  const handleWebDateConfirm = useCallback(params => {
    setIsWebDatePickerVisible(false);
    setSelectedWebDate(params.date);
  }, []);

  const closeLowerCourtDropdown = useCallback(() => {
    setIsLowerCourtDropdownOpen(false);
  }, []);

  const openImagePreview = image => {
    setImageModalVisible(true);
    setSelectedImage(image);
  };

  const handleSearchSubmit = values => {
    setIsLoading(true);
    setIsSearchExecuted(false);

    fetchCaseListData(values)
      .then(result => {
        setCaseInfoList(result.fulldatas);
        setIsFutureDateSearch(result.is_search_date_bigs);
        setIsLoading(false);
        setIsSearchExecuted(true);
      })
      .catch(err => console.log('handleSearchSubmit Error:', err));
  };

  const handleRefreshCauseList = values => {
    setIsLoading(true);
    setCaseInfoList([]);

    const searchDate =
      Platform.OS === 'web'
        ? selectedWebDate !== null
          ? moment(selectedWebDate).format('YYYY-MM-DD')
          : ''
        : selectedMobileDate !== null
          ? moment(selectedMobileDate).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD');

    const causeListRequest = {
      StartDate: searchDate,
      EndDate: searchDate,
      DivissionId: divisionId,
      geo_district_id: districtId,
      lower_court_id: values.lower_court_name,
    };

    refreshCauseList(causeListRequest)
      .then(result => {
        setIsLoading(false);
        setTimeout(() => alert(result.msg), 50);
      })
      .catch(err => console.log('handleRefreshCauseList Error:', err));
  };

  const handleCreateCaseFromCaseInfo = caseInfoId => {
    setIsLoading(true);

    submitCaseEntry(caseInfoId)
      .then(result => {
        if (!result) return;

        setIsLoading(false);
        Alert.alert('New Case Entry', result.msg);
        if (result.code === 200) {
          navigation.navigate('মামলা', { screen: 'TotalCaseScreen' });
        }
      })
      .catch(err => console.log('handleCreateCaseFromCaseInfo Error:', err));
  };

  // NOTE: This flow (submitCaseEntry / handleCreateCaseFromCaseInfo /
  // confirmCreateCaseFromCaseInfo) mirrors the original file's "action"
  // table column, whose Cell was already commented out there, so it isn't
  // currently called from anywhere in this screen's JSX. Kept intact
  // (renamed only) in case Riaz wants to re-wire an "add case" button to
  // a table row later — wire confirmCreateCaseFromCaseInfo(item.case_info_id)
  // to that button's onPress.
  const confirmCreateCaseFromCaseInfo = caseInfoId => {
    confirmAlert({
      title: 'মামলা যোগ করার জন্য নিশ্চিত করুন',
      message: 'আপনি এই কাজ করতে নিশ্চিত "এটা কি আপনার মামলা ??".',
      buttons: [
        { label: 'হ্যাঁ', onClick: () => handleCreateCaseFromCaseInfo(caseInfoId) },
        { label: 'না' },
      ],
    });
  };

  return (
    <View
      style={
        Platform.OS === 'web' && windowWidth >= 600
          ? globalStyles.mainContainer
          : globalStyles.mainContainerMobile
      }
    >
      <LeftBar>
        <LeftBarItem
          selectcolor="true"
          name="দৈনিক কার্যতালিকা"
          stack="দৈনিক কার্যতালিকা"
          screen=""
        />
        <LeftBarItemAdmin
          activeScreen={route.name}
          name="অফলাইন কার্যতালিকা"
          stack="দৈনিক কার্যতালিকা"
          screen="DailyCaseListScreen"
        />
        <LeftBarItemAdmin
          activeScreen={route.name}
          name="অনলাইন কার্যতালিকা ✔️"
          stack="দৈনিক কার্যতালিকা"
          screen="OnlineCaseListScreen"
        />
      </LeftBar>

      <View
        style={
          Platform.OS === 'web' && windowWidth >= 600
            ? globalStyles.bodyContainer
            : globalStyles.bodyContainerMobile
        }
      >
        <TouchableWithoutFeedback onPress={closeLowerCourtDropdown}>
          <View style={{ backgroundColor: '#fff' }}>
            <View style={Platform.OS === 'web' && windowWidth >= 600 ? styles.headerFixed : null}>
              <KeyboardAvoidingView style={styles.containerSearch} behavior="padding" enabled>
                <WavyTopBar customStyles={[styles.backgroundImage, { zIndex: -1 }]} />

                <Formik
                  enableReinitialize
                  initialValues={{
                    search_date: '',
                    division_name: '',
                    district_name: '',
                    lower_court_name: '',
                    type_of_adalot: '',
                    dairyName: '',
                  }}
                  validationSchema={searchFormValidationSchema}
                  onSubmit={handleSearchSubmit}
                >
                  {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                    <View style={styles.formGrid}>
                      {/* Division */}
                      <View style={styles.FormGroup}>
                        <View style={styles.pickerWrapper}>
                          <Picker
                            selectedValue={divisionId ?? 0}
                            style={isPlaceholderValue(divisionId) ? styles.pickerP2 : styles.picker2}
                            onValueChange={itemValue => {
                              setDivisionId(itemValue);
                              setDistrictId(null);
                              setCourtCategoryId(null);
                              setLowerCourtOptions([]);
                              setSelectedCourtId(null);
                              setSelectedCourtName('');
                              setPoliceStationId(null);
                              setPoliceStationOptions([]);
                              loadDistrictsForDivision(itemValue);
                              handleChange('division_name')(itemValue);
                            }}
                            mode="dropdown"
                          >
                            <Picker.Item label="বিভাগ" value="0" color="red" />
                            {divisions?.map((item, index) => (
                              <Picker.Item
                                color="#333"
                                label={item.division_name_bng.toString()}
                                value={item.geo_division_id.toString()}
                                key={index}
                              />
                            ))}
                          </Picker>
                          {touched.division_name && errors.division_name ? (
                            <ErrorText>{errors.division_name}</ErrorText>
                          ) : null}
                        </View>
                      </View>

                      {/* District */}
                      <View style={styles.FormGroup}>
                        <View style={styles.pickerWrapper}>
                          <Picker
                            selectedValue={districtId ?? 0}
                            style={isPlaceholderValue(districtId) ? styles.pickerP2 : styles.picker2}
                            onValueChange={(itemValue, itemIndex) => {
                              setDistrictId(itemValue);
                              setDistrictName(districts[itemIndex - 1]?.district_name_bng);
                              setCourtCategoryId(null);
                              setLowerCourtOptions([]);
                              setSelectedCourtId(null);
                              setSelectedCourtName('');
                              setPoliceStationId(null);
                              setPoliceStationOptions([]);
                              fetchCourtsByDistrict(itemValue);
                              setCourtCategoryOptions(
                                METRO_DISTRICT_IDS.includes(Number(itemValue))
                                  ? METRO_COURT_CATEGORIES
                                  : DISTRICT_COURT_CATEGORIES,
                              );
                              handleChange('district_name')(itemValue);
                            }}
                            mode="dropdown"
                          >
                            <Picker.Item label="জেলা" value="0" color="red" />
                            {districts.map((item, index) => (
                              <Picker.Item
                                color="#333"
                                label={item.district_name_bng.toString()}
                                value={item.geo_district_id.toString()}
                                key={index}
                              />
                            ))}
                          </Picker>
                          {touched.district_name && errors.district_name ? (
                            <ErrorText>{errors.district_name}</ErrorText>
                          ) : null}
                        </View>
                      </View>

                      {/* Court category (district vs. metro hierarchy) */}
                      <View style={styles.FormGroup}>
                        <View style={styles.pickerWrapper}>
                          <Picker
                            selectedValue={courtCategoryId ?? '0'}
                            style={[
                              isPlaceholderValue(courtCategoryId) ? styles.pickerP2 : styles.picker2,
                              IS_DESKTOP_FORM ? { width: 150 } : null,
                            ]}
                            dropdownIconColor="#1e3a8a"
                            onValueChange={itemValue => {
                              const selectedCategory = courtCategoryOptions.find(
                                item => item.id === itemValue,
                              );
                              if (selectedCategory) {
                                filterLowerCourtsByOriginIds(selectedCategory.office_origin_ids);
                              }
                              setCourtCategoryId(itemValue);
                              setSelectedCourtId(null);
                              setSelectedCourtName('');
                              setPoliceStationId(null);
                              setPoliceStationOptions([]);
                              setFieldValue('lower_court_name', '');
                              handleChange('name')(itemValue);
                            }}
                            mode="dropdown"
                          >
                            <Picker.Item label="আদালতের ধরন" value="0" color="red" />
                            {courtCategoryOptions.map((item, index) => (
                              <Picker.Item
                                key={index}
                                label={`${item.name}`}
                                value={item.id.toString()}
                                color="#333"
                              />
                            ))}
                          </Picker>
                          {touched.type_of_adalot && errors.type_of_adalot ? (
                            <ErrorText>{errors.type_of_adalot}</ErrorText>
                          ) : null}
                        </View>
                      </View>

                      {/* Lower court */}
                      <View style={styles.FormGroup}>
                        <View style={styles.pickerWrapper}>
                          <View
                            style={{
                              flexDirection: 'row',
                              height: windowWidth >= 600 ? 27 : 40,
                              zIndex: 100,
                              width: '100%',
                            }}
                          >
                            <CustomDropdown
                              data={lowerCourtOptionsWithPlaceholder}
                              selectedValue={selectedCourtId || ''}
                              onSelect={value => {
                                const actualValue = value === '' ? null : value;
                                setSelectedCourtId(actualValue);
                                setFieldValue('lower_court_name', actualValue ? String(actualValue) : '');
                                const selectedCourt = lowerCourtOptions?.find(
                                  item =>
                                    String(item.value) === String(actualValue) ||
                                    String(item.id) === String(actualValue),
                                );
                                setSelectedCourtName(selectedCourt?.office_name_bng || '');
                                setPoliceStationId(null);
                                if (Number(courtCategoryId) === 1 && actualValue) {
                                  loadPoliceStationOptions(actualValue);
                                } else {
                                  setPoliceStationOptions([]);
                                }
                              }}
                              placeholder="আদালত নির্বাচন করুন"
                              placeholderTextColor="#989898"
                              selectedTextColor="#333333"
                              dropdownButtonStyle={{
                                width: responsiveFieldWidth,
                                height: windowWidth >= 600 ? 27 : 40,
                                minHeight: windowWidth >= 600 ? 27 : 40,
                                zIndex: 100,
                              }}
                              selectOptionsWidth="auto"
                              dropdownListStyle={{}}
                              itemStyle={{}}
                              itemTextStyle={{}}
                              searchPlaceholder="আদালত সার্চ করুন..."
                              showSearch={null}
                              maxHeight={390}
                              zIndex={9999999}
                              onOpenChange={setIsLowerCourtDropdownOpen}
                            />
                          </View>
                        </View>
                        {!isLowerCourtDropdownOpen && touched.lower_court_name && errors.lower_court_name ? (
                          <ErrorText>{errors.lower_court_name}</ErrorText>
                        ) : null}
                      </View>

                      {/* Police station / GR diary (only for the diary-based flow) */}
                      {courtCategoryId == 1 && (
                        <View style={styles.FormGroup}>
                          <View style={styles.pickerWrapper}>
                            <Picker
                              selectedValue={policeStationId ?? '0'}
                              style={isPlaceholderValue(policeStationId) ? styles.pickerP2 : styles.picker2}
                              dropdownIconColor="#1e3a8a"
                              onValueChange={itemValue => {
                                setPoliceStationId(itemValue);
                                handleChange('dairyName')(itemValue);
                              }}
                              mode="dropdown"
                            >
                              <Picker.Item label="থানা/অঞ্চল নির্বাচন করুন" value="0" color="#f00" />
                              {policeStationOptions.map((item, index) => (
                                <Picker.Item
                                  key={index}
                                  label={`- ${item.label}`}
                                  value={item.value.toString()}
                                  color="#333"
                                />
                              ))}
                            </Picker>
                            {touched.dairyName && errors.dairyName ? (
                              <ErrorText>{errors.dairyName}</ErrorText>
                            ) : null}
                          </View>
                        </View>
                      )}

                      {/* Search date */}
                      <View style={[styles.FormGroup, { marginTop: IS_DESKTOP_FORM ? 0 : 100 }]}>
                        <View style={{ flexDirection: 'row' }}>
                          {Platform.OS === 'web' ? (
                            <TouchableOpacity
                              onPress={() => setIsWebDatePickerVisible(true)}
                              style={styles.sectionStyle}
                            >
                              <TextInput
                                style={styles.inputs1}
                                value={
                                  selectedWebDate !== null
                                    ? toBanglaNumerals(moment(selectedWebDate).format('DD-MM-YYYY'))
                                    : ''
                                }
                                onChangeText={handleChange('search_date')}
                                onBlur={handleBlur('search_date')}
                                placeholder="DD-MM-YYYY"
                                underlineColorAndroid="transparent"
                                onTouchStart={showDatePicker}
                              />
                              <Image
                                source={{ uri: 'https://bdjudgecourt.com/assets/calendar.png' }}
                                style={styles.imageStyle}
                              />
                            </TouchableOpacity>
                          ) : (
                            <TextInput
                              style={styles.inputs1}
                              value={
                                selectedMobileDate !== null
                                  ? toBanglaNumerals(moment(selectedMobileDate).format('DD-MM-YYYY'))
                                  : toBanglaNumerals(moment().format('DD-MM-YYYY'))
                              }
                              onChangeText={handleChange('search_date')}
                              onBlur={handleBlur('search_date')}
                              placeholder="DD-MM-YYYY"
                              underlineColorAndroid="transparent"
                              onTouchStart={showDatePicker}
                            />
                          )}
                        </View>

                        {Platform.OS === 'web' ? (
                          <View style={{ justifyContent: 'center', flex: 1, alignItems: 'center' }}>
                            <DatePickerModal
                              locale={LOCALE}
                              mode="single"
                              visible={isWebDatePickerVisible}
                              onDismiss={handleWebDateDismiss}
                              date={selectedWebDate}
                              onConfirm={handleWebDateConfirm}
                              onChange={handleWebDateConfirm}
                              validRange={{
                                startDate: PAST_DATE_LIMIT,
                                disabledDates: [FUTURE_DATE_LIMIT],
                              }}
                              style={{ width: 300, height: 400 }}
                              presentationStyle="formSheet"
                            />
                          </View>
                        ) : (
                          <DateTimePickerModal
                            isVisible={isMobileDatePickerVisible}
                            mode="date"
                            date={selectedMobileDate ?? new Date()}
                            onConfirm={handleConfirmMobileDate}
                            onCancel={hideDatePicker}
                          />
                        )}

                        {touched.search_date && errors.search_date ? (
                          <ErrorText>{errors.search_date}</ErrorText>
                        ) : null}
                      </View>

                      <View
                        style={[
                          styles.buttonRow,
                          IS_DESKTOP_FORM ? null : { flexDirection: 'row', justifyContent: 'center', gap: 4 },
                        ]}
                      >
                        <View style={styles.Topview}>
                          <AppBtn
                            title="অনুসন্ধান "
                            onPress={handleSubmit}
                            width={IS_DESKTOP_FORM ? 80 : 120}
                            fontwidth="bold"
                            disabled={isLoading}
                          />
                        </View>
                        <View style={styles.halnagadbtn}>
                          <AppBtn
                            title="হালনাগাদ"
                            onPress={() => handleRefreshCauseList(values)}
                            BGcolor="#102020"
                            width={IS_DESKTOP_FORM ? 80 : 120}
                            disabled={isLoading}
                          />
                        </View>
                      </View>
                    </View>
                  )}
                </Formik>
              </KeyboardAvoidingView>

              {isLoading ? (
                <View style={{ height: 700, paddingVertical: 200 }}>
                  <ActivityIndicator size="large" />
                </View>
              ) : (
                <View style={styles.wrapper}>
                  {/*
                    TODO: The "images" tab toggle button is currently disabled
                    (Riaz: re-enable here if/when the offline-cause-list image
                    view should be reachable again). The "images" content
                    branch below still works if activeTab is set
                    programmatically.
                  */}
                  <View style={styles.tabWrapper}>
                    <Pressable
                      style={({ pressed }) => [
                        styles.tabButton,
                        activeTab === 'files' && styles.activeTabButton,
                        pressed && { opacity: 0.8 },
                      ]}
                      onPress={() => setActiveTab('files')}
                      hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
                    >
                      <Text style={[styles.tabText, activeTab === 'files' && styles.activeTabText]}>
                          অনলাইন কার্যতালিকা ✔️
                      </Text>
                      {activeTab === 'files' && <AnimatedArrow />}
                    </Pressable>
                  </View>

                  <View style={styles.contentBox}>
                    {activeTab === 'images' ? (
                      <>
                        {causeListImages.length === 0 && isSearchExecuted && !isLoading ? (
                          <Text
                            style={[styles.Topheader, { marginTop: windowHeight - windowHeight / 2 - 100 }]}
                          >
                            এই পেইজ এখনো আপডেট কারা হয়নি
                          </Text>
                        ) : (
                          <>
                            <View style={{ zIndex: -1, marginTop: -40 }}>
                              <Text style={styles.Topheader}>
                                {selectedCourtName ? `${selectedCourtName},` : selectedCourtName}
                                {districtName}
                              </Text>
                              <Text style={styles.Topheader}>দৈনিক কার্যতালিকা</Text>
                              {!isLoading && Platform.OS === 'web' ? (
                                <Text style={styles.TopheaderDate}>
                                  তারিখঃ{' '}
                                  {selectedWebDate !== null
                                    ? toBanglaDateTimeString(moment(selectedWebDate).format('DD-MM-YYYY'))
                                    : ''}
                                  ,{' '}
                                  {selectedWebDate !== null
                                    ? toBanglaDayName(moment(selectedWebDate).format('dddd'))
                                    : ''}{' '}
                                  (সর্বশেষ হালনাগাদঃ{' '}
                                  {caseInfoList?.length > 0
                                    ? toBanglaDateTimeString(caseInfoList[0]?.halnagatTime)
                                    : ''}
                                  )
                                </Text>
                              ) : null}
                            </View>
                            <ImageGallery grAllData={causeListImages} date={selectedWebDate} />
                            <View style={{ marginBottom: 100 }} />
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <View style={{ zIndex: -1, marginTop: IS_DESKTOP_FORM ? -40 : 40 }}>
                          <Text style={styles.Topheader}>
                            {caseInfoList?.length > 0
                              ? `${caseInfoList[0]?.office_name_bng},`
                              : selectedCourtName + (selectedCourtName ? ',' : '')}{' '}
                            {caseInfoList?.length > 0 ? caseInfoList[0]?.district_name_bng : districtName}
                          </Text>
                          <Text style={styles.Topheader}>দৈনিক কার্যতালিকা</Text>
                          {!isLoading && Platform.OS === 'web' ? (
                            <Text style={styles.TopheaderDate}>
                              তারিখঃ{' '}
                              {selectedWebDate !== null
                                ? toBanglaDateTimeString(moment(selectedWebDate).format('DD-MM-YYYY'))
                                : ''}
                              ,{' '}
                              {selectedWebDate !== null
                                ? toBanglaDayName(moment(selectedWebDate).format('dddd'))
                                : ''}{' '}
                              (সর্বশেষ হালনাগাদঃ{' '}
                              {caseInfoList?.length > 0
                                ? toBanglaDateTimeString(caseInfoList[0]?.halnagatTime)
                                : ''}
                              )
                            </Text>
                          ) : null}
                        </View>

                        {caseInfoList && Platform.OS === 'web' && windowWidth >= 600 ? (
                          <View style={[styles.webView, { marginTop: 5 }]}>
                            <Table borderStyle={{ borderWidth: 0.3, borderColor: '#dfdddd' }}>
                              <Row
                                data={TABLE_COLUMN_TITLES}
                                widthArr={TABLE_COLUMN_WIDTHS}
                                style={styles.head}
                                textStyle={[styles.text1]}
                              />
                            </Table>
                            <View style={{ height: windowHeight / 2 + 200 }}>
                              <ScrollView style={[styles.dataWrapper, { marginBottom: 80 }]}>
                                <Table
                                  borderStyle={{
                                    borderWidth: 0.3,
                                    borderColor: '#dfdddd',
                                    paddingBottom: 100,
                                  }}
                                >
                                  {caseInfoList.map((item, index) => (
                                    <TableWrapper
                                      key={index}
                                      style={[
                                        styles.row,
                                        {
                                          paddingVertical: 0,
                                          backgroundColor: index % 2 ? '#F7F6E7' : '#fff',
                                        },
                                      ]}
                                    >
                                      <Cell
                                        width={TABLE_COLUMN_WIDTHS[0]}
                                        data={
                                          isFutureDateSearch === 1
                                            ? toBanglaNumerals(index + 1)
                                            : toBanglaNumerals(item.serial_no)
                                        }
                                        textStyle={styles.text}
                                      />
                                      <Cell
                                        width={TABLE_COLUMN_WIDTHS[1]}
                                        data={
                                          `${item.type_name} - ${toBanglaNumerals(item.case_number)}` +
                                          `${item.arising_out_of}/${toBanglaNumerals(item.case_year)} ` +
                                          `${item.upazila_name_bng}`
                                        }
                                        textStyle={styles.text}
                                      />
                                      <Cell
                                        width={TABLE_COLUMN_WIDTHS[2]}
                                        data={isFutureDateSearch === 1 ? item.result : item.activities}
                                        textStyle={styles.text}
                                      />
                                      <Cell
                                        width={TABLE_COLUMN_WIDTHS[3]}
                                        data={
                                          isFutureDateSearch === 1
                                            ? ''
                                            : item.next_date === '01-01-1970'
                                              ? ''
                                              : toBanglaNumerals(item.next_date)
                                        }
                                        textStyle={styles.text}
                                      />
                                      <Cell
                                        width={TABLE_COLUMN_WIDTHS[4]}
                                        data={isFutureDateSearch === 1 ? '' : item.result}
                                        textStyle={styles.text}
                                      />
                                    </TableWrapper>
                                  ))}
                                </Table>
                                <View style={{ marginBottom: 200 }} />

                                {caseInfoList?.length === 0 && isSearchExecuted && !isLoading ? (
                                  <Text
                                    style={[
                                      styles.Topheader,
                                      { marginTop: windowHeight - windowHeight / 2 - 100 },
                                    ]}
                                  >
                                    এই পেইজ এখনো আপডেট কারা হয়নি
                                  </Text>
                                ) : null}
                              </ScrollView>
                            </View>
                          </View>
                        ) : null}
                      </>
                    )}
                  </View>
                </View>
              )}
            </View>
          </View>
        </TouchableWithoutFeedback>
      </View>

      <Modal visible={imageModalVisible} transparent animationType="fade">
        <View style={styles.modalContainer}>
          <Pressable style={styles.closeButton} onPress={() => setImageModalVisible(false)}>
            <Text style={styles.closeText}>✕</Text>
          </Pressable>
          <TouchableOpacity activeOpacity={1} style={styles.fullImageWrapper}>
            {selectedImage?.file_path && (
              <Image
                style={styles.fullImage}
                source={{ uri: 'https://api.bdjudgecourt.com' + selectedImage.file_path }}
              />
            )}
          </TouchableOpacity>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  text: { textAlign: 'center', fontSize: 14, fontFamily: 'SolaimanLipi', paddingVertical: 5 },
  text1: { textAlign: 'center', fontSize: 14, fontFamily: 'SolaimanLipi', fontWeight: 'bold' },
  row: { flexDirection: 'row' },
  dataWrapper: { marginTop: -1, marginRight: -25 },

  sectionStyle: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ccc',
    height: windowWidth >= 600 ? 27 : 41,
    borderRadius: 5,
    margin: 0,
    zIndex: -1,
    width: IS_DESKTOP_FORM ? 'auto' : '100%',
  },
  imageStyle: {
    padding: 10,
    margin: 5,
    height: 25,
    width: 25,
    resizeMode: 'stretch',
    alignItems: 'center',
  },
  webView: {
    flex: 1,
    width: TABLE_WIDTH,
    paddingBottom: 10,
    paddingHorizontal: 0,
    marginBottom: 10,
  },
  head: {
    height: 30,
    backgroundColor: 'khaki',
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
  },
  headerFixed: {
    top: 0,
    left: 0,
    zIndex: 2,
    position: 'sticky',
    backgroundColor: '#fff',
  },
  picker2: {
    height: windowWidth >= 600 ? 27 : 40,
    color: '#333',
    width: IS_DESKTOP_FORM ? 100 : '100%',
    backgroundColor: '#fff',
    fontFamily: 'SolaimanLipi',
    fontSize: 14,
  },
  pickerP2: {
    height: windowWidth >= 600 ? 27 : 40,
    color: '#989898',
    width: IS_DESKTOP_FORM ? 100 : '100%',
    backgroundColor: '#fff',
    fontFamily: 'SolaimanLipi',
    fontSize: 14,
  },
  inputs1: {
    zIndex: -1,
    height: windowWidth >= 600 ? 27 : 40,
    padding: 5,
    borderRadius: 2,
    fontSize: 13,
    width: IS_DESKTOP_FORM ? 90 : '100%',
    flex: IS_DESKTOP_FORM ? 0 : 1,
    backgroundColor: '#fff',
    fontFamily: 'SolaimanLipi',
  },
  FormGroup: {
    width: IS_DESKTOP_FORM ? 'auto' : IS_TABLET_FORM ? '50%' : '100%',
    paddingHorizontal: 5,
    marginBottom: Platform.OS === 'web' && windowWidth >= 600 ? -2 : 10,
    minWidth: 0,
  },
  Topview: {
    marginTop: Platform.OS === 'web' && windowWidth >= 600 ? -7 : 10,
    marginLeft: Platform.OS === 'web' && windowWidth >= 600 ? 0 : 35,
  },
  halnagadbtn: {
    marginTop: Platform.OS === 'web' && windowWidth >= 600 ? -7 : 10,
    marginLeft: Platform.OS === 'web' && windowWidth >= 600 ? 10 : 15,
  },
  pickerWrapper: {
    width: IS_DESKTOP_FORM ? 'auto' : '100%',
    borderColor: '#ccc',
    borderWidth: 1,
    borderRadius: 4,
    backgroundColor: 'transparent',
  },
  formGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    marginTop: 5,
    marginBottom: 5,
  },
  buttonRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: IS_DESKTOP_FORM ? 'auto' : '100%',
    justifyContent: IS_DESKTOP_FORM ? 'flex-start' : 'space-between',
    marginTop: 5,
  },
  containerSearch: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    alignItems: 'center',
    paddingVertical: 0,
  },
  backgroundImage: {
    flex: 1,
    height: 'auto',
    position: 'absolute',
    width: Dimensions.get('window').width - 190,
    bottom: 0,
    zIndex: -1,
  },
  Topheader: {
    textAlign: 'center',
    fontSize: 20,
    color: '#000',
    fontFamily: 'SolaimanLipi',
  },
  TopheaderDate: {
    textAlign: 'center',
    fontSize: 16,
    color: '#000',
    fontFamily: 'SolaimanLipi',
  },
  wrapper: {
    marginTop: 20,
    marginHorizontal: 16,
    zIndex: -1,
  },
  tabWrapper: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingBottom: 0,
    zIndex: 2,
    position: 'relative',
  },
  tabButton: {
    paddingVertical: 7,
    paddingHorizontal: 10,
    marginRight: 10,
    borderRadius: 5,
    backgroundColor: '#e2e8f0',
    borderWidth: 1,
    borderColor: '#cbd5e1',
    cursor: 'pointer',
    zIndex: 999,
  },
  activeTabButton: {
    backgroundColor: '#000',
    shadowColor: '#3b82f6',
    color: 'white',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 8,
    zIndex: 999,
  },
  tabText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#475569',
    fontFamily: 'SolaimanLipi',
  },
  activeTabText: {
    color: '#FFF',
    fontWeight: '600',
    fontFamily: 'SolaimanLipi',
    backgroundColor: '#000',
  },
  contentBox: {
    backgroundColor: '#ffffff',
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    paddingTop: 0,
    height: windowHeight - 100,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.95)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullImageWrapper: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullImage: {
    width: '99%',
    height: '90%',
    resizeMode: 'contain',
  },
  closeButton: {
    position: 'absolute',
    top: 50,
    right: 25,
    zIndex: 10,
    backgroundColor: 'rgba(255,255,255,0.15)',
    width: 42,
    height: 42,
    borderRadius: 21,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeText: {
    color: '#fff',
    fontSize: 22,
    fontWeight: 'bold',
  },
});
